import React from "react";
import {
  Drawer,
  Box,
  Avatar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Logout,
  ChevronRight,
  ChevronLeft,
  Close as CloseIcon,
} from "@mui/icons-material";
import { RefreshCw, Users } from "lucide-react";
import logo from "../../assets/spookfish_logo.png";

function Sidebar({
  isMobile,
  sidebarOpen,
  setSidebarOpen,
  sidebarCollapsed,
  setSidebarCollapsed,
  darkMode,
  theme,
  currentUser,
  activeView,
  setActiveView,
  sidebarItems,
  expiringCount,
  refreshing,
  fetchAllData,
  logout,
  formatToIST,
}) {
  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'permanent'}
      open={isMobile ? sidebarOpen : true}
      onClose={() => setSidebarOpen(false)}
      sx={{
        width: sidebarCollapsed ? 72 : 280,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: sidebarCollapsed ? 72 : 280,
          transition: 'width 0.3s ease',
          boxSizing: 'border-box',
          bgcolor: darkMode ? '#0E1626' : '#FFFFFF',
          borderRight: darkMode ? '1px solid #16213A' : '1px solid #EAF0F4',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        },
      }}
    >
      <Box sx={{ p: sidebarCollapsed ? 1 : 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? 0 : 1.5 }}>
            <Avatar
              src={logo}
              variant="rounded"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                boxShadow: '0px 4px 8px rgba(12,133,153,0.15)',
              }}
            />
            {!sidebarCollapsed && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                  Spookfish
                </Typography>
                <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                  License Manager
                </Typography>
              </Box>
            )}
          </Box>
          {isMobile && (
            <IconButton onClick={() => setSidebarOpen(false)}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>

        {!sidebarCollapsed && (
        <Box sx={{ mb: 4 }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            bgcolor: darkMode ? '#16213A' : '#EAF3FB',
            borderRadius: 3,
          }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
              {currentUser.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                {currentUser}
              </Typography>
              <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}>
                {currentUser === 'admin' ? 'Administrator' : 'User'}
              </Typography>
            </Box>
            <IconButton size="small" onClick={fetchAllData} disabled={refreshing} sx={{ color: darkMode ? '#EAF3FB' : 'inherit' }}>
              <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
            </IconButton>
          </Box>
        </Box>
        )}

        <List sx={{ px: 1 }}>
          {sidebarItems.map((item) => (
            <ListItem
              button
              key={item.text}
              onClick={() => {
                setActiveView(item.view);
                if (isMobile) setSidebarOpen(false);
              }}
              sx={{
                borderRadius: 3,
                mb: 0.5,
                px: sidebarCollapsed ? 1 : 2,
                py: 1.5,
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                bgcolor: activeView === item.view
                  ? alpha(theme.palette.primary.main, darkMode ? 0.2 : 0.08)
                  : 'transparent',
                borderLeft: activeView === item.view ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: activeView === item.view
                    ? alpha(theme.palette.primary.main, darkMode ? 0.3 : 0.12)
                    : alpha(theme.palette.primary.main, darkMode ? 0.1 : 0.04),
                },
              }}
            >
              <ListItemIcon sx={{
                minWidth: sidebarCollapsed ? 0 : 40,
                color: activeView === item.view
                  ? 'primary.main'
                  : darkMode ? '#93A2BC' : 'text.secondary',
              }}>
                {item.icon}
              </ListItemIcon>
              {!sidebarCollapsed && (
                <>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontWeight: activeView === item.view ? 600 : 500,
                      color: activeView === item.view
                        ? 'primary.main'
                        : darkMode ? '#EAF3FB' : 'text.primary',
                    }}
                  />
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {item.badge !== null && item.badge > 0 && (
                      <Chip
                        label={item.badge}
                        size="small"
                        sx={{
                          bgcolor: activeView === item.view
                            ? 'primary.main'
                            : darkMode ? '#16213A' : '#EAF0F4',
                          color: activeView === item.view
                            ? 'white'
                            : darkMode ? '#EAF3FB' : 'text.secondary',
                          fontWeight: 600,
                          height: 24,
                          minWidth: 24,
                        }}
                      />
                    )}
                    {item.view === 'licenses' && expiringCount > 0 && (
                      <Chip
                        label={`⚠ ${expiringCount}`}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.warning.main, 0.15),
                          color: 'warning.main',
                          fontWeight: 700,
                          height: 24,
                          minWidth: 24,
                          fontSize: '0.65rem',
                          animation: 'pulse 2s ease-in-out infinite',
                        }}
                      />
                    )}
                  </Box>
                </>
              )}
            </ListItem>
          ))}
        </List>

        <Divider sx={{ my: 3, borderColor: darkMode ? '#16213A' : '#D8E1E8' }} />

        <List sx={{ px: 1 }}>
          {currentUser === "admin" && (
            <ListItem
              button
              onClick={() => {
                setActiveView("users");
                if (isMobile) setSidebarOpen(false);
              }}
              sx={{
                borderRadius: 3,
                mb: 0.5,
                px: sidebarCollapsed ? 1 : 2,
                py: 1.5,
                justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                bgcolor: activeView === "users"
                  ? alpha(theme.palette.primary.main, darkMode ? 0.2 : 0.08)
                  : 'transparent',
                borderLeft: activeView === "users" ? `3px solid ${theme.palette.primary.main}` : '3px solid transparent',
                transition: 'all 0.2s ease',
              }}
            >
              <ListItemIcon sx={{
                minWidth: sidebarCollapsed ? 0 : 40,
                color: activeView === "users"
                  ? 'primary.main'
                  : darkMode ? '#93A2BC' : 'text.secondary',
              }}>
                <Users size={20} />
              </ListItemIcon>
              {!sidebarCollapsed && (
                <ListItemText
                  primary="Users"
                  primaryTypographyProps={{
                    fontWeight: activeView === "users" ? 600 : 500,
                    color: activeView === "users"
                      ? 'primary.main'
                      : darkMode ? '#EAF3FB' : 'text.primary',
                  }}
                />
              )}
            </ListItem>
          )}
          <ListItem
            button
            onClick={logout}
            sx={{
              borderRadius: 3,
              px: sidebarCollapsed ? 1 : 2,
              py: 1.5,
              justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
              color: 'error.main',
              borderLeft: '3px solid transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(theme.palette.error.main, darkMode ? 0.2 : 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: sidebarCollapsed ? 0 : 40, color: 'error.main' }}>
              <Logout />
            </ListItemIcon>
            {!sidebarCollapsed && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontWeight: 500,
                  color: 'error.main',
                }}
              />
            )}
          </ListItem>
        </List>

        {!sidebarCollapsed && (
          <Box sx={{ mt: 4, p: 2, bgcolor: darkMode ? '#16213A' : '#EAF3FB', borderRadius: 3 }}>
            <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary', display: 'block', mb: 1 }}>
              System Status
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 500, color: darkMode ? '#EAF3FB' : '#0E1B2C' }}>
                All systems operational
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: darkMode ? '#93A2BC' : 'text.secondary', display: 'block', mt: 1 }}>
              Last updated: {formatToIST(new Date().toISOString())}
            </Typography>
          </Box>
        )}

        {!isMobile && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: sidebarCollapsed ? 'center' : 'flex-end' }}>
            <Tooltip title={sidebarCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}>
              <IconButton
                size="small"
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                sx={{ color: darkMode ? '#93A2BC' : 'text.secondary' }}
              >
                {sidebarCollapsed ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    </Drawer>
  );
}

export default Sidebar;
